import { useState } from 'react'
import { cn } from '../../../utils/cn'

interface QuestionOption {
  id: string
  label: string
  value: number
}

interface AssessmentQuestionProps {
  questionNumber: number
  questionText: string
  options?: QuestionOption[]
  selectedValue?: string
  onSelect?: (value: string) => void
  className?: string
}

const defaultOptions: QuestionOption[] = [
  { id: 'strongly-agree', label: 'Strongly Agree', value: 5 },
  { id: 'agree', label: 'Agree', value: 4 },
  { id: 'neutral', label: 'Neutral', value: 3 },
  { id: 'disagree', label: 'Disagree', value: 2 },
  { id: 'strongly-disagree', label: 'Strongly Disagree', value: 1 },
]

export function AssessmentQuestion({
  questionNumber,
  questionText,
  options = defaultOptions,
  selectedValue,
  onSelect,
  className,
}: AssessmentQuestionProps) {
  const [selected, setSelected] = useState<string | undefined>(selectedValue)

  const handleSelect = (optionId: string) => {
    setSelected(optionId)
    onSelect?.(optionId)
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Question Header */}
      <div className='space-y-3'>
        <p className='text-sm font-medium text-gray-500'>
          Question {questionNumber}
        </p>
        <h3 className='text-lg font-medium leading-relaxed text-gray-900'>
          {questionText}
        </h3>
      </div>

      {/* Options */}
      <div className='space-y-3'>
        {options.map(option => {
          const isSelected = selected === option.id

          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id)}
              className={cn(
                'relative flex w-full items-center justify-between rounded-lg border bg-white px-5 py-4 text-left transition-all hover:border-gray-300',
                isSelected ? 'border-gray-300' : 'border-gray-200'
              )}
            >
              {/* Active indicator */}
              {isSelected && (
                <div className='absolute left-0 top-1/2 h-10 w-1 -translate-y-1/2 rounded-r-full bg-gray-900' />
              )}

              <span
                className={cn(
                  'text-base',
                  isSelected ? 'font-medium text-gray-900' : 'text-gray-600'
                )}
              >
                {option.label}
              </span>

              {/* Radio button */}
              <div
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
                  isSelected ? 'border-[#F97316]' : 'border-gray-300'
                )}
              >
                {isSelected && (
                  <div className='h-2.5 w-2.5 rounded-full bg-[#F97316]' />
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
